import { PartyPopper, Check } from 'lucide-react'
import capitalize from 'lodash/capitalize'
import { Button, Card } from '@bpinternal/ui-kit-next'
import { Link as RouterLink } from '@tanstack/react-router'
import { BillingVersion, Plan } from './types'

type WelcomePageProps = {
  plan: Extract<Plan, 'plus' | 'team'>
  billingVersion: BillingVersion
}

const PLAN_INCLUSIONS: Record<Extract<Plan, 'plus' | 'team'>, Record<BillingVersion, string[]>> = {
  plus: {
    v1: [],
    v2: [],
    v3: [
      '5,000 /mo. messages & events.',
      '2 bots.',
      'Always Alive for 1 bot.',
      '2 collaborator seats.',
      '100,000 Table Rows.',
      '1GB Vector DB Storage',
      '10GB File Storage.',
      'Live Agent Handoff',
      'Visual knowledge base indexing',
      'Live-chat support',
    ],
  },
  team: {
    v1: [
      '250,000 /mo. messages & events.',
      '20 bots.',
      'Always Alive for 20 bots.',
      '5 collaborator seats.',
      '1,000,000 Table Rows.',
      '2GB Vector DB Storage.',
      '10GB File Storage.',
      'Live Agent Handoff',
      'Removing "Powered by Botpress" in WebChat',
      '90 days of data retention',
      '1000 API Req/sec',
      'Live-chat support',
      'Real-Time Collaboration',
      'Custom Analytics',
    ],
    v2: [
      '250,000 /mo. messages & events.',
      '20 bots.',
      'Always Alive for 20 bots.',
      '5 collaborator seats.',
      '1,000,000 Table Rows.',
      '2GB Vector DB Storage.',
      '10GB File Storage.',
      'Live Agent Handoff',
      'Removing "Powered by Botpress" in WebChat',
      '90 days of data retention',
      '1000 API Req/sec',
      'Live-chat support',
      'Real-Time Collaboration',
      'Custom Analytics',
    ],
    v3: [
      '50,000 /mo. messages & events.',
      '3 bots.',
      'Always Alive for 3 bots.',
      '3 collaborator seats.',
      '100,000 Table Rows.',
      '2GB Vector DB Storage.',
      '10GB File Storage',
      'RBAC',
      'Real-time collaboration',
      'Advanced support',
    ],
  },
}

const TeamMessage = () => {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p>
        With the Team plan, you now get higher limits, real-time collaboration, and priority support to help you move
        faster.
      </p>
      <p>
        Your Customer Success Manager will reach out soon to help you get started and support you as you grow with
        Botpress.
      </p>
      <p>Have a question? Reach us through live chat anytime.</p>
    </div>
  )
}

const PlusMessage = () => {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm">
        It&apos;s all about keeping your building experience simple, efficient, and feature-packed: exactly what you
        need to build and ship AI agents. Fast.
      </p>

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium">Limited time</h2>
        <p className="text-sm">
          Get hands-on support in a free 1:1 session with a Botpress Expert. Build your first bot or optimize an
          existing one.{' '}
          <a
            className="text-brand-600"
            href="https://calendly.com/d/ctcd-gvb-2rp/30-minute-ai-agent-bootstrapping-session?month=2025-09"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book your AI Agent Session
          </a>
          .
        </p>
      </div>
    </div>
  )
}

const WelcomeInclusions = ({
  plan,
  billingVersion,
}: {
  plan: Extract<Plan, 'plus' | 'team'>
  billingVersion: BillingVersion
}) => {
  const inclusions = PLAN_INCLUSIONS[plan][billingVersion]

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">Included</h3>
      {inclusions.map((feature) => (
        <div key={feature} className="flex items-center gap-2">
          <Check className="h-5 flex-none stroke-[1.5px]" />
          <p className="text-sm text-muted-foreground">{feature}</p>
        </div>
      ))}
    </div>
  )
}

export const WelcomePage = ({ plan, billingVersion }: WelcomePageProps) => {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-gray-2">
      <Card className="flex max-w-[874px] gap-8 bg-background p-10">
        <div className="flex basis-1/2 flex-col items-start gap-7">
          <PartyPopper strokeWidth={'1'} />

          <div className="flex flex-col">
            <h1 className="text-2xl font-medium">
              Welcome to <br />
              Botpress {capitalize(plan)}.
            </h1>
          </div>

          {plan === 'team' && <TeamMessage />}
          {plan === 'plus' && <PlusMessage />}

          <RouterLink to="/" preload="viewport">
            <Button>
              <img className="w-[12px] brightness-0 invert filter" src="/bp-logo.svg" />
              Return to Workspace
            </Button>
          </RouterLink>
        </div>

        <div className="basis-1/2">
          <WelcomeInclusions plan={plan} billingVersion={billingVersion} />
        </div>
      </Card>
    </div>
  )
}
